
import { useEffect } from "react";
import { db } from "@/integrations/cloudflare/client";
import { parseMetadata } from "@/utils/blogPostUtils";
import { usePostEditorState } from "@/utils/postEditorUtils";
import { usePostEditorActions } from "@/hooks/usePostEditorActions";
import { PostEditorHookResult } from "@/types/postEditorTypes";

export type { PostEditorState } from "@/types/postEditorTypes";

export const usePostEditor = (postId?: string): PostEditorHookResult => {
  // Get state management functions
  const {
    state,
    isLoading,
    showDeleteDialog,
    setShowDeleteDialog,
    setTitle,
    setSlug,
    setExcerpt,
    setContent,
    setAuthor,
    setSeoDescription,
    setTheme,
    setCategories,
    setFeaturedImage,
    setFeaturedImageAlt,
    setFeaturedImageTitle,
    setIsLoading,
    setIsPending,
    setIsDraft,
    togglePublishStatus,
    toast
  } = usePostEditorState(postId);

  // Get action handlers
  const {
    handleImageUpload,
    handleDeletePost,
    handleSaveAsDraft,
    handleSubmit
  } = usePostEditorActions(
    postId,
    state,
    setIsPending,
    setShowDeleteDialog,
    setIsDraft,
    toast
  );

  // Fetch post data if editing
  useEffect(() => {
    if (postId && isLoading) {
      const fetchPost = async () => {
        try {
          const { data, error } = await db
            .from('blog_posts')
            .select('*')
            .eq('id', postId)
            .single();
            
          if (error) {
            throw error;
          }
          
          if (data) {
            // Set post data
            setTitle(data.title || "");
            setSlug(data.slug || "");
            setExcerpt(data.excerpt || "");
            setContent(data.content || "");
            if (data.featured_image) {
              setFeaturedImage(data.featured_image);
            }
            setIsDraft(!data.published);
            
            // Parse and set enhanced fields from metadata
            const metadata = parseMetadata(data.metadata);
            if (metadata.author) {
              setAuthor(metadata.author);
            }
            
            if (metadata.seo_description) {
              setSeoDescription(metadata.seo_description);
            }
            
            if (metadata.theme) {
              setTheme(metadata.theme);
            }
            
            if (metadata.categories && Array.isArray(metadata.categories)) {
              setCategories(metadata.categories);
            }
            
            if (metadata.featured_image_alt) {
              setFeaturedImageAlt(metadata.featured_image_alt);
            }
            
            if (metadata.featured_image_title) {
              setFeaturedImageTitle(metadata.featured_image_title);
            }
          }
          
          setIsLoading(false);
        } catch (error: any) {
          console.error("Error loading post:", error);
          toast({
            title: "Error loading post",
            description: error.message || "Failed to load blog post",
            variant: "destructive",
          });
          setIsLoading(false);
        }
      };
      
      fetchPost();
    }
  }, [postId]);

  return {
    ...state,
    isLoading,
    showDeleteDialog,
    setShowDeleteDialog,
    setTitle,
    setSlug,
    setExcerpt,
    setContent,
    setAuthor,
    setSeoDescription,
    setTheme,
    setCategories,
    setFeaturedImage,
    setFeaturedImageAlt,
    setFeaturedImageTitle,
    setIsLoading,
    setIsPending,
    setIsDraft,
    togglePublishStatus,
    handleImageUpload,
    handleDeletePost,
    handleSaveAsDraft,
    handleSubmit,
  };
};
